import {
    Paragraph,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    HeightRule,
    AlignmentType,
    TableLayoutType
} from "docx";
import {
    FULL_WIDTH,
    fixColor,
    fitImage,
    drawPlaceholderIcon,
    drawOrgText,
    buildOrgTree,
    getMaxOrgDepth,
    createStripeHeader,
    createStripeFooter,
    getContrastColor
} from "@/utils/wordGenerator/common.js";
import {api_baseURL} from "@/utils/init.js";

// --- Helper: Generate Org Chart Image Data ---
const generateOrgChartImage = async (orgChart) => {
    const treeData = buildOrgTree(orgChart.members);
    if (treeData.length === 0) return null;

    const NODE_WIDTH = 110;
    const DETAILS_HEIGHT = 60;
    const IMG_HEIGHT = 110;
    const NODE_HEIGHT = IMG_HEIGHT + DETAILS_HEIGHT;
    const GAP_X = 20;

    const getSubtreeWidth = (node) => {
        if (!node.children || node.children.length === 0) return NODE_WIDTH;
        const childrenW = node.children.reduce((acc, c) => acc + getSubtreeWidth(c), 0);
        const childrenGap = (node.children.length - 1) * GAP_X;
        return Math.max(NODE_WIDTH, childrenW + childrenGap);
    };

    const maxDepth = getMaxOrgDepth(treeData);
    const canvasTargetHeight = 1100;
    const totalNodesHeight = maxDepth * NODE_HEIGHT;
    const availableGapSpace = Math.max(50, canvasTargetHeight - totalNodesHeight - 150);
    const LEVEL_GAP = maxDepth > 1 ? Math.min(80, availableGapSpace / (maxDepth - 1)) : 80;

    const totalWidth = treeData.reduce((acc, r) => acc + getSubtreeWidth(r), 0) + (treeData.length - 1) * GAP_X;
    const totalHeight = Math.max(canvasTargetHeight, totalNodesHeight + (maxDepth - 1) * LEVEL_GAP + 100);

    const canvas = document.createElement('canvas');
    const DPR = 3;
    canvas.width = (totalWidth + 100) * DPR;
    canvas.height = totalHeight * DPR;
    const ctx = canvas.getContext('2d');
    ctx.scale(DPR, DPR);

    ctx.fillStyle = "transparent";
    ctx.fillRect(0, 0, totalWidth + 100, totalHeight);

    const rawBoxColor = fixColor(orgChart.style.colorScheme?.body || "4a4a4a");
    const boxColor = "#" + rawBoxColor;
    const contrastColor = "#" + fixColor(getContrastColor(rawBoxColor));

    const labelStyle = orgChart.style.label || {};
    const textStyle = orgChart.style.text || {};

    const drawNode = async (node, x, y, w) => {
        const nodeX = x + (w - NODE_WIDTH) / 2;
        const nodeY = y;

        // 1. Photo
        if (node.profilePicturePath) {
            const imgUrl = `${api_baseURL}profilePicture?tmid=${node.teamMemberId}`;
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.src = imgUrl;
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                });
                ctx.drawImage(img, nodeX, nodeY, NODE_WIDTH, IMG_HEIGHT);
            } catch (e) {
                drawPlaceholderIcon(ctx, nodeX, nodeY, IMG_HEIGHT);
            }
        } else {
            drawPlaceholderIcon(ctx, nodeX, nodeY, IMG_HEIGHT);
        }

        // 2. Details Box
        ctx.fillStyle = boxColor;
        ctx.fillRect(nodeX, nodeY + IMG_HEIGHT, NODE_WIDTH, DETAILS_HEIGHT);

        // Name & Role Rendering using shared Helper
        drawOrgText(ctx, {
            name: node.teamMemberName || "N/A",
            role: node.role || "N/A",
            nameStyle: labelStyle,
            roleStyle: textStyle,
            x: nodeX + NODE_WIDTH / 2,
            y: nodeY + IMG_HEIGHT + 6,
            width: NODE_WIDTH,
            contrastColor: contrastColor
        });

        // 3. Connectors
        if (node.children && node.children.length > 0) {
            const subtreeWidths = node.children.map(getSubtreeWidth);
            const childrenTotalW = subtreeWidths.reduce((a, b) => a + b, 0) + (node.children.length - 1) * GAP_X;
            let currentX = x + (w - childrenTotalW) / 2;
            const childY = y + NODE_HEIGHT + LEVEL_GAP;

            ctx.strokeStyle = "#888888";
            ctx.lineWidth = 1.5;

            // vertical down from parent - EXIT FROM BOTTOM OF ENTIRE NODE
            ctx.beginPath();
            ctx.moveTo(nodeX + NODE_WIDTH / 2, nodeY + NODE_HEIGHT);
            ctx.lineTo(nodeX + NODE_WIDTH / 2, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
            ctx.stroke();

            const firstChildCenter = currentX + subtreeWidths[0] / 2;
            const lastChildCenter = currentX + (childrenTotalW - subtreeWidths[subtreeWidths.length - 1] / 2);
            ctx.beginPath();
            ctx.moveTo(firstChildCenter, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
            ctx.lineTo(lastChildCenter, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
            ctx.stroke();

            for (let i = 0; i < node.children.length; i++) {
                const childW = subtreeWidths[i];
                const childX = currentX + childW / 2;
                ctx.beginPath();
                ctx.moveTo(childX, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
                ctx.lineTo(childX, childY);
                ctx.stroke();
                await drawNode(node.children[i], currentX, childY, childW);
                currentX += childW + GAP_X;
            }
        }
    };

    let startX = 50;
    for (const root of treeData) {
        const w = getSubtreeWidth(root);
        await drawNode(root, startX, 40, w);
        startX += w + GAP_X;
    }

    const blob = await new Promise(r => canvas.toBlob(r, 'image/png', 1.0));
    const arrayBuffer = await blob.arrayBuffer();
    return {
        data: new Uint8Array(arrayBuffer),
        width: canvas.width / DPR,
        height: canvas.height / DPR
    };
};

export const createOrgChartTemp1 = async (orgData, startPage) => {
    const sections = [];
    const rawBannerColor = fixColor(orgData.style.colorScheme?.header || "4a4a4a");
    const rawFooterColor = fixColor(orgData.style.colorScheme?.footer || "4a4a4a");

    const BODY_HEIGHT = 15230;

    // Header Table
    const headerTable = createStripeHeader(orgData.title, rawBannerColor);

    // Body Table
    const chartImgData = await generateOrgChartImage(orgData);
    const chartRun = chartImgData ? fitImage(chartImgData, FULL_WIDTH * 0.95, BODY_HEIGHT * 0.95, "contain") : null;

    const bodyRow = new TableRow({
        height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
        children: [
            new TableCell({
                children: chartRun ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [chartRun] })] : [new Paragraph("No chart data")]
            })
        ]
    });

    // Footer Table
    const footerTable = createStripeFooter(startPage, rawFooterColor);

    const mainTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({ children: [new TableCell({ children: [headerTable] })] }),
            bodyRow,
            new TableRow({ children: [new TableCell({ children: [footerTable] })] })
        ]
    });

    sections.push({
        properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
        children: [mainTable]
    });

    return sections;
};
